import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, CircleAlert, Image as ImageIcon, LayoutTemplate, Link2, Loader2, Lock, Plus, RefreshCw, Reply, Trash2, Upload, UserMinus,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames } from '@/lib/utils';
import {
  ESTADO_PLANTILLA, IDIOMAS, NOMBRE_IDIOMA, TEXTO_BOTON_BAJA,
  type BorradorPlantilla, type Idioma, type PlantillaResumen, type PreviaWhatsApp, type UsoPlantilla,
} from '@/lib/comunicaciones';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import BurbujaWhatsApp from '@/components/comunicaciones/BurbujaWhatsApp';

/**
 * Plantillas de WhatsApp: todas las de la cuenta, y crear nuevas para las
 * campañas. Las creadas desde el CRM se pueden ampliar con otro idioma o
 * borrar; las demás (las de Hostify: códigos, check-in, limpiezas…) solo se
 * ven, porque sostienen los avisos a huéspedes.
 */
export default function PlantillasPage() {
  const { data, loading, error, refresh } = useApi(() => api.getPlantillasWhatsApp(), []);
  const [soloCrm, setSoloCrm] = useState(true);
  const [editor, setEditor] = useState<{ nombre?: string; ocupados?: string[]; uso?: UsoPlantilla } | null>(null);
  /** Qué idioma se ve en la burbuja de cada plantilla; sin elegir, el primero (el español). */
  const [verIdioma, setVerIdioma] = useState<Record<string, string>>({});

  const lista = useMemo(() => {
    const todas = data || [];
    const filtradas = soloCrm ? todas.filter(p => p.delCrm) : todas;
    return [...filtradas].sort((a, b) => Number(b.delCrm) - Number(a.delCrm) || a.nombre.localeCompare(b.nombre));
  }, [data, soloCrm]);

  const borrar = async (p: PlantillaResumen) => {
    if (!window.confirm(`¿Borrar la plantilla «${p.nombre}» en todos sus idiomas? Las campañas que la usen dejarán de poder enviarse, y Meta puede no dejar reutilizar ese nombre durante un tiempo.`)) return;
    try {
      await api.borrarPlantillaWhatsApp(p.nombre);
      refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'No se pudo borrar');
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div className="flex items-center gap-2">
          <LayoutTemplate size={24} className="text-sereno-dark shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Plantillas de WhatsApp</h1>
            <p className="text-sm text-sereno-dark/40 mt-0.5">Lo que se puede mandar por WhatsApp a quien no te ha escrito: cada plantilla pasa la revisión de Meta.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={refresh} disabled={loading} className="btn-ghost flex items-center gap-2 text-sm">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            <span className="hidden sm:inline">Actualizar</span>
          </button>
          <button onClick={() => setEditor({})} className="btn-primary flex items-center gap-2 text-sm">
            <Plus size={16} /> Nueva plantilla
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 text-sm">
        <button type="button" onClick={() => setSoloCrm(true)} className={classNames('px-3 py-1 rounded-full border', soloCrm ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60')}>
          Creadas desde el CRM {data && <span className="opacity-60">{data.filter(p => p.delCrm).length}</span>}
        </button>
        <button type="button" onClick={() => setSoloCrm(false)} className={classNames('px-3 py-1 rounded-full border', !soloCrm ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60')}>
          Todas {data && <span className="opacity-60">{data.length}</span>}
        </button>
      </div>

      {error && <div className="card text-sm text-red-600 mb-4">{error}</div>}
      {loading && !data ? <LoadingSpinner /> : lista.length === 0 ? (
        <div className="card text-center py-10 text-sm text-sereno-dark/50">
          {soloCrm ? 'Todavía no hay plantillas creadas desde el CRM.' : 'No hay plantillas en la cuenta.'}{' '}
          <button type="button" className="underline" onClick={() => setEditor({})}>Crear la primera</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lista.map(p => (
            <div key={p.nombre} className="card p-4 space-y-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-sereno-dark truncate">{p.nombre}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <span className={classNames('badge', p.categoria === 'MARKETING' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>{p.categoria.toLowerCase()}</span>
                    {p.delCrm
                      ? <><span className="badge bg-sky-100 text-sky-700">del CRM</span>{p.uso === 'leads' && <span className="badge bg-violet-100 text-violet-700">para leads</span>}{p.uso === 'portal' && <span className="badge bg-amber-100 text-amber-800" title="La manda el CRM al activar el portal de un propietario">del portal</span>}</>
                      : <span className="badge bg-slate-100 text-slate-500 flex items-center gap-1"><Lock size={10} /> Hostify u otra app</span>}
                  </div>
                </div>
                {p.delCrm && (
                  <div className="flex gap-1">
                    <button type="button" onClick={() => setEditor({ nombre: p.nombre, ocupados: p.idiomas.map(i => i.idioma.split('_')[0]), uso: p.uso })} className="btn-ghost border border-gray-200 text-xs px-2 py-1">Añadir idioma</button>
                    <button type="button" onClick={() => borrar(p)} className="p-1.5 text-sereno-dark/30 hover:text-red-600" title="Borrar en todos los idiomas"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {p.idiomas.map(i => {
                  const e = ESTADO_PLANTILLA[i.estado] || { texto: i.estado.toLowerCase(), clase: 'bg-slate-100 text-slate-600' };
                  const vista = (verIdioma[p.nombre] || p.idiomas[0]?.idioma) === i.idioma;
                  return (
                    <button
                      key={i.idioma}
                      type="button"
                      onClick={() => setVerIdioma(v => ({ ...v, [p.nombre]: i.idioma }))}
                      className={classNames('badge', e.clase, vista ? 'ring-2 ring-offset-1 ring-sereno-dark/40' : 'opacity-70 hover:opacity-100')}
                      title={[p.idiomas.length > 1 ? 'Ver esta versión' : '', i.motivo ? `Motivo de Meta: ${i.motivo}` : ''].filter(Boolean).join(' · ') || undefined}
                      aria-pressed={vista}
                    >
                      {i.idioma} · {e.texto}{i.motivo ? ' ⓘ' : ''}
                    </button>
                  );
                })}
              </div>
              <BurbujaWhatsApp previa={p.idiomas.find(i => i.idioma === verIdioma[p.nombre])?.previa || p.previa} />
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={!!editor} onClose={() => setEditor(null)} title={editor?.nombre ? `Añadir idioma a «${editor.nombre}»` : 'Nueva plantilla de WhatsApp'} size="2xl">
        {editor && <EditorPlantilla nombreFijo={editor.nombre} usoFijo={editor.uso} ocupados={editor.ocupados || []} onCreada={() => { setEditor(null); refresh(); }} />}
      </Modal>
    </div>
  );
}

function borradorInicial(nombre = '', idioma: Idioma = 'es', uso: UsoPlantilla = 'campanas'): BorradorPlantilla {
  return {
    uso,
    nombre,
    idioma,
    cabecera: { tipo: 'ninguna' },
    cuerpo: uso === 'leads' ? 'Hola {{1}}, te escribimos de Sereno ' : '¡Hola {{1}}! ',
    ejemplos: ['Laura'],
    pie: '',
    // A un lead se le escribe de uno en uno y puede contestar BAJA: no hace falta el botón
    botones: uso === 'leads' ? [] : [{ tipo: 'baja', texto: TEXTO_BOTON_BAJA[idioma] }],
  };
}

const USOS: { id: UsoPlantilla; texto: string; ayuda: string }[] = [
  { id: 'campanas', texto: 'Para campañas', ayuda: 'Marketing. Se manda a muchos suscriptores a la vez desde Campañas.' },
  { id: 'leads', texto: 'Para leads', ayuda: 'Utilidad. Abre la conversación con un lead desde su ficha, de uno en uno; {{1}} es su nombre. Meta puede pasarla a marketing si la ve comercial.' },
];

/** minúsculas, sin tildes, espacios a guion bajo: lo único que admite Meta. */
function limpiarNombre(t: string): string {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
}

function huecosDelCuerpo(cuerpo: string): number {
  const n = [...cuerpo.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map(m => Number(m[1]));
  return n.length ? Math.max(...n) : 0;
}

/** El borrador como lo verá quien revisa, con los ejemplos en los huecos. */
function previaDe(b: BorradorPlantilla): PreviaWhatsApp {
  const con = (t: string, ej: string[]) => t.replace(/\{\{\s*(\d+)\s*\}\}/g, (m, n) => ej[Number(n) - 1] || m);
  return {
    ...(b.cabecera.tipo === 'texto' ? { cabecera: con(b.cabecera.texto, [b.cabecera.ejemplo]) } : {}),
    ...(b.cabecera.tipo === 'imagen' && b.cabecera.url ? { imagen: b.cabecera.url } : {}),
    cuerpo: con(b.cuerpo, b.ejemplos),
    ...(b.pie ? { pie: b.pie } : {}),
    botones: [
      ...b.botones.filter(x => x.tipo === 'enlace').map(x => ({ tipo: 'URL', texto: x.texto || '…' })),
      ...b.botones.filter(x => x.tipo !== 'enlace').map(x => ({ tipo: 'QUICK_REPLY', texto: x.texto || '…' })),
    ],
  };
}

function EditorPlantilla({ nombreFijo, usoFijo, ocupados, onCreada }: { nombreFijo?: string; usoFijo?: UsoPlantilla; ocupados: string[]; onCreada: () => void }) {
  const primerLibre = (IDIOMAS.find(i => !ocupados.includes(i)) || 'es') as Idioma;
  const [b, setB] = useState<BorradorPlantilla>(() => borradorInicial(nombreFijo, primerLibre, usoFijo || 'campanas'));
  const [revision, setRevision] = useState<{ errores: string[]; avisos: string[] } | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const cuerpoRef = useRef<HTMLTextAreaElement>(null);
  const fichero = useRef<HTMLInputElement>(null);

  const n = huecosDelCuerpo(b.cuerpo);
  const cambiar = (c: Partial<BorradorPlantilla>) => setB(prev => ({ ...prev, ...c }));

  // La revisión la hace el servidor con las mismas reglas que al crear
  const clave = JSON.stringify(b);
  useEffect(() => {
    if (!b.nombre) { setRevision(null); return; }
    const t = setTimeout(() => { api.revisarPlantillaWhatsApp(b).then(setRevision).catch(() => setRevision(null)); }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const anadirHueco = () => {
    const ta = cuerpoRef.current;
    const marca = `{{${n + 1}}}`;
    const i = ta?.selectionStart ?? b.cuerpo.length;
    cambiar({ cuerpo: b.cuerpo.slice(0, i) + marca + b.cuerpo.slice(i), ejemplos: [...b.ejemplos.slice(0, n), ''] });
  };

  const subir = async (f?: File) => {
    if (!f) return;
    setSubiendo(true);
    try {
      const { url } = await api.subirImagenCampana(f);
      cambiar({ cabecera: { tipo: 'imagen', url } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen');
    } finally {
      setSubiendo(false);
      if (fichero.current) fichero.current.value = '';
    }
  };

  const enviar = async () => {
    setEnviando(true);
    setError(null);
    try {
      const r = await api.crearPlantillaWhatsApp({ ...b, ejemplos: b.ejemplos.slice(0, n) });
      window.alert(r.estado === 'APPROVED'
        ? 'Meta la ha aprobado al momento: ya se puede usar en una campaña.'
        : 'Enviada a revisión. Meta suele contestar en minutos; te avisaremos por Telegram (tema Sistemas).');
      onCreada();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear');
    } finally {
      setEnviando(false);
    }
  };

  const setBoton = (i: number, x: BorradorPlantilla['botones'][number]) => cambiar({ botones: b.botones.map((y, j) => (j === i ? x : y)) });
  const hayErrores = !b.nombre || !!revision?.errores.length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
      <div className="space-y-4">
        <div>
          <label className="label">Para qué es</label>
          <div className="flex gap-1.5">
            {USOS.map(u => (
              <button key={u.id} type="button" disabled={!!nombreFijo && u.id !== b.uso}
                onClick={() => {
                  if (u.id === b.uso) return;
                  // Se cambia lo que aún es el texto de partida; lo escrito a mano se respeta
                  const inicial = borradorInicial(b.nombre, b.idioma, b.uso);
                  const nuevo = borradorInicial(b.nombre, b.idioma, u.id);
                  cambiar({
                    uso: u.id,
                    ...(b.cuerpo === inicial.cuerpo ? { cuerpo: nuevo.cuerpo } : {}),
                    ...(JSON.stringify(b.botones) === JSON.stringify(inicial.botones) ? { botones: nuevo.botones } : {}),
                  });
                }}
                className={classNames('text-xs px-2.5 py-1 rounded-lg border disabled:opacity-40', b.uso === u.id ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60')}>
                {u.texto}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-sereno-dark/35 mt-1">{USOS.find(u => u.id === b.uso)?.ayuda}{nombreFijo ? ' Es el de la plantilla ya creada.' : ''}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-3">
          <div>
            <label className="label">Nombre</label>
            <input className="input font-mono text-sm" value={b.nombre} disabled={!!nombreFijo} placeholder="novedades_primavera"
              onChange={e => cambiar({ nombre: limpiarNombre(e.target.value) })} />
            <p className="text-[11px] text-sereno-dark/35 mt-1">Minúsculas y guiones bajos. Es el mismo en todos los idiomas.</p>
          </div>
          <div>
            <label className="label">Idioma</label>
            <select className="input" value={b.idioma} onChange={e => {
              const idioma = e.target.value as Idioma;
              // El texto del botón de baja sigue al idioma si no se ha cambiado a mano
              cambiar({ idioma, botones: b.botones.map(x => (x.tipo === 'baja' && Object.values(TEXTO_BOTON_BAJA).includes(x.texto) ? { ...x, texto: TEXTO_BOTON_BAJA[idioma] } : x)) });
            }}>
              {IDIOMAS.map(i => <option key={i} value={i} disabled={ocupados.includes(i)}>{NOMBRE_IDIOMA[i]}{ocupados.includes(i) ? ' (ya existe)' : ''}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Cabecera (opcional)</label>
          <div className="flex gap-1.5 mb-2">
            {(['ninguna', 'texto', 'imagen'] as const).map(t => (
              <button key={t} type="button" onClick={() => cambiar({ cabecera: t === 'ninguna' ? { tipo: t } : t === 'texto' ? { tipo: t, texto: '', ejemplo: '' } : { tipo: t, url: '' } })}
                className={classNames('text-xs px-2.5 py-1 rounded-lg border', b.cabecera.tipo === t ? 'bg-sereno-dark text-white border-sereno-dark' : 'border-gray-200 text-sereno-dark/60')}>
                {t === 'ninguna' ? 'Ninguna' : t === 'texto' ? 'Texto' : 'Imagen'}
              </button>
            ))}
          </div>
          {b.cabecera.tipo === 'texto' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className="input text-sm" maxLength={60} value={b.cabecera.texto} placeholder="Novedades de Sereno" onChange={e => cambiar({ cabecera: { ...b.cabecera as any, texto: e.target.value } })} />
              {/\{\{\s*1\s*\}\}/.test(b.cabecera.texto) && (
                <input className="input text-sm" value={b.cabecera.ejemplo} placeholder="Ejemplo de {{1}}" onChange={e => cambiar({ cabecera: { ...b.cabecera as any, ejemplo: e.target.value } })} />
              )}
            </div>
          )}
          {b.cabecera.tipo === 'imagen' && (
            <div className="flex items-center gap-2">
              <div className="w-16 h-12 rounded bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                {b.cabecera.url ? <img src={b.cabecera.url} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-sereno-dark/30" />}
              </div>
              <button type="button" onClick={() => fichero.current?.click()} disabled={subiendo} className="btn-ghost border border-gray-200 text-xs flex items-center gap-1.5 px-3 py-1.5">
                {subiendo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Imagen de ejemplo
              </button>
              <input ref={fichero} type="file" accept="image/jpeg,image/png" className="hidden" onChange={e => subir(e.target.files?.[0])} />
              <span className="text-[11px] text-sereno-dark/35">Solo para la revisión: la de verdad se elige en cada campaña.</span>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="label">Cuerpo</label>
            <button type="button" onClick={anadirHueco} className="text-xs text-sereno-dark/60 hover:text-sereno-dark">+ Añadir hueco {`{{${n + 1}}}`}</button>
          </div>
          <textarea ref={cuerpoRef} className="input text-sm" rows={5} maxLength={1024} value={b.cuerpo}
            onChange={e => {
              const cuerpo = e.target.value;
              const k = huecosDelCuerpo(cuerpo);
              cambiar({ cuerpo, ejemplos: Array.from({ length: k }, (_, i) => b.ejemplos[i] || '') });
            }} />
          <p className="text-[11px] text-sereno-dark/35 mt-1">
            {b.cuerpo.length}/1024 · *negrita*, _cursiva_ · {'{{1}}'} suele ser el nombre de quien lo recibe.
          </p>
          {n > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {Array.from({ length: n }, (_, i) => (
                <input key={i} className="input text-sm" placeholder={`Ejemplo de {{${i + 1}}}`} value={b.ejemplos[i] || ''}
                  onChange={e => cambiar({ ejemplos: Array.from({ length: n }, (_, j) => (j === i ? e.target.value : b.ejemplos[j] || '')) })} />
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="label">Pie (opcional)</label>
          <input className="input text-sm" maxLength={60} value={b.pie} placeholder="Sereno · Costa de la Luz" onChange={e => cambiar({ pie: e.target.value })} />
        </div>

        <div className="space-y-2">
          <label className="label">Botones</label>
          {b.botones.map((x, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="mt-2 text-sereno-dark/40">{x.tipo === 'enlace' ? <Link2 size={15} /> : x.tipo === 'baja' ? <UserMinus size={15} /> : <Reply size={15} />}</span>
              {/* En el móvil el texto y la dirección van uno debajo del otro: en fila la dirección no cabe */}
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row gap-2">
                <input className="input text-sm w-full sm:w-40 sm:shrink-0" maxLength={25} value={x.texto} placeholder="Texto" onChange={e => setBoton(i, { ...x, texto: e.target.value })} />
                {x.tipo === 'enlace' && (
                  <div className="flex-1 min-w-0 space-y-1">
                    <input className="input text-sm" value={x.url} placeholder="https://tusereno.es/casas o https://tusereno.es/{{1}}" onChange={e => setBoton(i, { ...x, url: e.target.value })} />
                    {/\{\{\s*1\s*\}\}$/.test(x.url) && (
                      <input className="input text-sm" value={x.ejemplo} placeholder="Ejemplo completo: https://tusereno.es/casas" onChange={e => setBoton(i, { ...x, ejemplo: e.target.value })} />
                    )}
                  </div>
                )}
                {x.tipo === 'baja' && <span className="text-[11px] text-sereno-dark/40 sm:mt-2 flex-1">Quien lo pulse se da de baja de WhatsApp</span>}
              </div>
              <button type="button" onClick={() => cambiar({ botones: b.botones.filter((_, j) => j !== i) })} className="p-2 text-sereno-dark/30 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
          <div className="flex flex-wrap gap-1.5">
            <button type="button" disabled={b.botones.filter(x => x.tipo === 'enlace').length >= 2} onClick={() => cambiar({ botones: [...b.botones, { tipo: 'enlace', texto: 'Ver casas', url: 'https://tusereno.es', ejemplo: '' }] })} className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-sereno-dark/60 disabled:opacity-40">+ Enlace</button>
            <button type="button" disabled={b.botones.length >= 10} onClick={() => cambiar({ botones: [...b.botones, { tipo: 'respuesta', texto: '' }] })} className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-sereno-dark/60 disabled:opacity-40">+ Respuesta rápida</button>
            <button type="button" disabled={b.botones.some(x => x.tipo === 'baja')} onClick={() => cambiar({ botones: [...b.botones, { tipo: 'baja', texto: TEXTO_BOTON_BAJA[b.idioma] }] })} className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-sereno-dark/60 disabled:opacity-40">+ Botón de baja</button>
          </div>
        </div>
      </div>

      <div className="space-y-3 lg:sticky lg:top-0 self-start">
        <div className="text-xs font-semibold uppercase tracking-wide text-sereno-dark/50">Así la verá quien la revisa</div>
        <BurbujaWhatsApp previa={previaDe(b)} />
        {revision && (revision.errores.length > 0 || revision.avisos.length > 0) && (
          <ul className="space-y-1">
            {revision.errores.map((e, i) => <li key={`e${i}`} className="text-xs text-red-600 flex items-start gap-1.5"><CircleAlert size={13} className="mt-0.5 shrink-0" />{e}</li>)}
            {revision.avisos.map((a, i) => <li key={`a${i}`} className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle size={13} className="mt-0.5 shrink-0" />{a}</li>)}
          </ul>
        )}
        {error && <div className="text-sm text-red-600">{error}</div>}
        <button type="button" onClick={enviar} disabled={enviando || hayErrores} className="btn-primary w-full flex items-center justify-center gap-2">
          {enviando && <Loader2 size={16} className="animate-spin" />} Enviar a revisión de Meta
        </button>
        <p className="text-[11px] text-sereno-dark/35">Categoría Marketing. Meta suele contestar en minutos; si la rechaza, el motivo sale en la lista y por Telegram.</p>
      </div>
    </div>
  );
}
